<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { LineChart, Plus, Trash2, RefreshCw, BarChart2, Hash } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

interface XYSeries {
  type: 'bar' | 'line'
  dataText: string
}

// Estados visuales
const chartTitle = ref('Ingresos Trimestrales')
const xAxisText = ref('T1, T2, T3, T4')
const yAxisTitle = ref('Ingresos en miles ($)')
const yAxisMin = ref<number | null>(0)
const yAxisMax = ref<number | null>(100)
const series = ref<XYSeries[]>([
  { type: 'bar', dataText: '25, 40, 65, 80' },
  { type: 'line', dataText: '30, 45, 60, 90' }
])

// Acciones
const addSeries = (type: 'bar' | 'line') => {
  series.value.push({ type, dataText: '10, 20, 30, 40' })
}

const removeSeries = (idx: number) => {
  series.value.splice(idx, 1)
}

// Generar Mermaid
const generateMermaid = () => {
  let code = `xychart-beta\n`
  if (chartTitle.value.trim()) {
    code += `    title "${chartTitle.value.trim().replace(/"/g, '')}"\n`
  }
  
  const xArr = xAxisText.value.split(',').map(s => s.trim()).filter(s => s)
  if (xArr.length > 0) {
    code += `    x-axis [${xArr.join(', ')}]\n`
  } else {
    code += `    x-axis [X]\n`
  }
  
  const safeYTitle = yAxisTitle.value.trim().replace(/"/g, '')
  let yAxisStr = `    y-axis`
  
  if (safeYTitle) yAxisStr += ` "${safeYTitle}"`
  if (yAxisMin.value !== null && yAxisMin.value !== undefined && 
      yAxisMax.value !== null && yAxisMax.value !== undefined) {
    yAxisStr += ` ${yAxisMin.value} --> ${yAxisMax.value}`
  }
  
  if (yAxisStr !== `    y-axis`) {
    code += `${yAxisStr}\n`
  }

  series.value.forEach(s => {
    // Validar que los datos sean números válidos
    const dataArr = s.dataText.split(',').map(n => {
      const parsed = parseFloat(n.trim())
      return isNaN(parsed) ? 0 : parsed
    })
    
    if (dataArr.length > 0) {
      code += `    ${s.type} [${dataArr.join(', ')}]\n`
    }
  })
  
  editableCode.value = code
}

// Sincronizar desde Código a Visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newSeries: XYSeries[] = []
  let newTitle = ''
  let newX = ''
  let newYTitle = ''
  let newYMin: number | null = null
  let newYMax: number | null = null
  
  for (const line of lines) {
    const t = line.trim()
    if (t.startsWith('title ')) {
      newTitle = t.replace('title ', '').replace(/"/g, '').trim()
    } else if (t.startsWith('x-axis ')) {
      const match = t.match(/\[(.*?)\]/)
      if (match) newX = match[1]
    } else if (t.startsWith('y-axis ')) {
      const titleMatch = t.match(/"([^"]+)"/)
      if (titleMatch) newYTitle = titleMatch[1]
      
      const rangeMatch = t.match(/([\d.-]+)\s*-->\s*([\d.-]+)/)
      if (rangeMatch) {
        newYMin = parseFloat(rangeMatch[1])
        newYMax = parseFloat(rangeMatch[2])
      }
    } else if (t.startsWith('bar ') || t.startsWith('line ')) {
      const type = t.startsWith('bar') ? 'bar' : 'line'
      const match = t.match(/\[(.*?)\]/)
      if (match) {
        newSeries.push({ type, dataText: match[1] })
      }
    }
  }
  
  chartTitle.value = newTitle
  xAxisText.value = newX
  yAxisTitle.value = newYTitle
  yAxisMin.value = newYMin
  yAxisMax.value = newYMax
  if (newSeries.length > 0) series.value = newSeries
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([chartTitle, xAxisText, yAxisTitle, yAxisMin, yAxisMax, series], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Gráfico XY (Barras y Líneas)"
    description="Crea gráficos de series temporales o categorías combinando barras y líneas."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <LineChart class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" variant="default" @click="addSeries('bar')"><BarChart2 class="w-4 h-4 mr-2" /> Añadir Barras</Button>
      <Button size="sm" variant="secondary" @click="addSeries('line')"><LineChart class="w-4 h-4 mr-2" /> Añadir Línea</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Configuración Principal -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2">Configuración General</h3>
          
          <div class="flex flex-col gap-1">
            <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Título del Gráfico</label>
            <input 
              v-model="chartTitle" 
              class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary font-medium"
              placeholder="Ej. Ingresos Mensuales"
            />
          </div>

          <div class="flex flex-col gap-1 mt-2">
            <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1 flex items-center gap-1">
              <Hash class="w-3 h-3"/> Eje X (Categorías separadas por coma)
            </label>
            <input 
              v-model="xAxisText" 
              class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ej. Ene, Feb, Mar, Abr"
            />
            <p class="text-[10px] text-muted-foreground mt-1 ml-1">Nota: Asegúrate de tener la misma cantidad de datos en cada serie que las categorías del Eje X.</p>
          </div>
        </div>

        <!-- Eje Y -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2">Configuración Eje Y</h3>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1 md:col-span-1">
              <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Título Eje Y</label>
              <input 
                v-model="yAxisTitle" 
                class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Ej. Cantidad"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Valor Mínimo (Opcional)</label>
              <input 
                type="number"
                v-model.number="yAxisMin" 
                class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Auto"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Valor Máximo (Opcional)</label>
              <input 
                type="number"
                v-model.number="yAxisMax" 
                class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Auto"
              />
            </div>
          </div>
        </div>

        <!-- Series de Datos -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2">Series de Datos</h3>

          <div v-if="series.length === 0" class="text-center text-muted-foreground py-8 border-2 border-dashed rounded-md">
            No hay series de datos. Añade Barras o Líneas.
          </div>

          <div class="flex flex-col gap-3">
            <div 
              v-for="(s, idx) in series" 
              :key="idx"
              class="flex items-start md:items-center gap-3 bg-muted/40 border p-3 rounded-md relative flex-col md:flex-row"
            >
              <div class="flex items-center gap-2 w-32 shrink-0">
                <BarChart2 v-if="s.type === 'bar'" class="w-5 h-5 text-blue-500" />
                <LineChart v-if="s.type === 'line'" class="w-5 h-5 text-emerald-500" />
                <select v-model="s.type" class="bg-transparent border-b font-bold text-sm focus:outline-none capitalize focus:border-primary">
                  <option value="bar">Barra</option>
                  <option value="line">Línea</option>
                </select>
              </div>

              <div class="flex flex-col gap-1 flex-1 w-full">
                <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Datos (Separados por coma)</label>
                <input 
                  v-model="s.dataText" 
                  class="bg-background border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono w-full"
                  placeholder="Ej. 10, 20, 30, 40"
                />
              </div>

              <button 
                @click="removeSeries(idx)" 
                class="md:ml-2 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-2 rounded-full absolute right-2 top-2 md:relative md:top-auto md:right-auto"
                title="Eliminar Serie"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
